import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Trash2, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { useCheckin } from "../context/CheckinContext";
import { createParent, apiError } from "../lib/api";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const dateRe = /^\d{4}-\d{2}-\d{2}$/;

export default function ParentRegister() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { flow, update } = useCheckin();
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [kids, setKids] = useState([{ name: "", birthday: "" }]);
  const [loading, setLoading] = useState(false);

  const phone = flow.parent?.phone_number || "";

  const addKid = () => setKids((k) => [...k, { name: "", birthday: "" }]);
  const removeKid = (i) => setKids((k) => k.filter((_, idx) => idx !== i));
  const setKid = (i, field, val) =>
    setKids((k) => k.map((c, idx) => (idx === i ? { ...c, [field]: val } : c)));

  const handleSubmit = async () => {
    if (!name.trim()) return toast.error(t("nameRequired"));
    if (!surname.trim()) return toast.error(t("surnameRequired"));
    for (const k of kids) {
      if (!k.name.trim()) return toast.error(t("childNameRequired"));
      if (!k.birthday.trim()) return toast.error(t("birthdayRequired"));
      if (!dateRe.test(k.birthday.trim())) return toast.error(t("useDateFormat"));
    }
    setLoading(true);
    try {
      const res = await createParent({
        name: name.trim(),
        surname: surname.trim(),
        phone_number: phone,
        children: kids.map((k) => ({ name: k.name.trim(), birthday: k.birthday.trim() })),
      });
      update({
        parent: { id: res.id, name: res.name, surname: res.surname, phone_number: res.phone_number },
        children: res.children,
        selected: res.children.map((c) => c.id),
        needsConsent: true,
      });
      navigate("/parent/select");
    } catch (e) {
      toast.error(apiError(e) || t("failedToRegister"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen lf-grain bg-[#FFEEBC]" data-testid="parent-register-page">
      <header className="mx-auto flex max-w-2xl items-center justify-between px-5 py-6">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/parent/phone")}
          className="flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold text-[#26422A] hover:bg-[#26422A]/10"
        >
          <ArrowLeft className="h-4 w-4" /> {t("back")}
        </button>
        <LanguageSwitcher />
      </header>

      <main className="mx-auto max-w-2xl px-5 pb-16">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#26422A]">
            <UserPlus className="h-6 w-6 text-[#FFEEBC]" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-black text-[#26422A]">
              {t("newRegistration")}
            </h1>
            <p className="text-sm font-medium text-[#26422A]">{phone}</p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl border-2 border-[#26422A]/40 bg-[#FFFFFF] p-6 shadow-lg"
        >
          <h2 className="mb-4 font-display text-lg font-bold text-[#26422A]">
            {t("yourInformation")}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="font-bold text-[#26422A]">{t("firstName")}</Label>
              <Input
                data-testid="reg-firstname-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("enterFirstName")}
                className="h-12 rounded-xl border-[#26422A]/60 bg-white font-semibold"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-bold text-[#26422A]">{t("lastName")}</Label>
              <Input
                data-testid="reg-lastname-input"
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                placeholder={t("enterLastName")}
                className="h-12 rounded-xl border-[#26422A]/60 bg-white font-semibold"
              />
            </div>
          </div>
        </motion.div>

        <div className="mt-6 space-y-4">
          <h2 className="font-display text-lg font-bold text-[#26422A]">{t("children")}</h2>
          {kids.map((kid, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border-2 border-[#26422A]/40 bg-[#FFFFFF] p-5"
              data-testid={`child-form-${i}`}
            >
              <div className="mb-3 flex items-center justify-between">
                <span className="font-bold text-[#26422A]">
                  {t("child")} {i + 1}
                </span>
                {kids.length > 1 && (
                  <button
                    data-testid={`remove-child-${i}`}
                    onClick={() => removeKid(i)}
                    className="text-[#5D372A] hover:opacity-70"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#26422A]">{t("childName")}</Label>
                  <Input
                    data-testid={`child-name-${i}`}
                    value={kid.name}
                    onChange={(e) => setKid(i, "name", e.target.value)}
                    placeholder={t("enterChildName")}
                    className="h-12 rounded-xl border-[#26422A]/60 bg-white font-semibold"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#26422A]">{t("birthday")}</Label>
                  <Input
                    data-testid={`child-birthday-${i}`}
                    type="date"
                    value={kid.birthday}
                    onChange={(e) => setKid(i, "birthday", e.target.value)}
                    className="h-12 rounded-xl border-[#26422A]/60 bg-white font-semibold"
                  />
                </div>
              </div>
            </motion.div>
          ))}

          <Button
            data-testid="add-child-btn"
            variant="outline"
            onClick={addKid}
            className="h-12 w-full rounded-xl border-2 border-dashed border-[#26422A] bg-transparent font-bold text-[#26422A] hover:bg-[#26422A]/10"
          >
            <Plus className="mr-2 h-5 w-5" /> {t("addAnotherChild")}
          </Button>
        </div>

        <Button
          data-testid="register-submit-btn"
          onClick={handleSubmit}
          disabled={loading}
          className="mt-8 h-14 w-full rounded-xl bg-[#26422A] text-base font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("register")}
        </Button>
      </main>
    </div>
  );
}
